import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  createNeighborhoodOccupant,
  deleteNeighborhoodOccupant,
  NeighborhoodOccupantPayload,
  NeighborhoodOccupantUpdatePayload,
  updateNeighborhoodOccupant,
} from 'app/frontend/base/api/housing'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { OccupancyData } from './utils'

export const neighborhoodOccupantsQueryKey = (guildId: number) => [
  'neighborhoodOccupants',
  { guildId },
]

export const useHousingMutations = (guildId: number) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const updateCache = (data: OccupancyData) => {
    queryClient.setQueryData(neighborhoodOccupantsQueryKey(guildId), data)
  }

  const onError = (error: Error) => displayErrorToast(error, t)

  const createChoice = useMutation({
    mutationFn: (payload: NeighborhoodOccupantPayload) =>
      createNeighborhoodOccupant(guildId, payload),
    onSuccess: (data) => {
      updateCache(data)
      toast.success(t('shared.changes-saved'))
    },
    onError,
  })

  const updateChoice = useMutation({
    mutationFn: ({
      occupantId,
      payload,
    }: {
      occupantId: number
      payload: NeighborhoodOccupantUpdatePayload
    }) => updateNeighborhoodOccupant(guildId, occupantId, payload),
    onSuccess: (data) => {
      updateCache(data)
      toast.success(t('shared.changes-saved'))
    },
    onError,
  })

  const deleteChoice = useMutation({
    mutationFn: ({
      rankedChoice,
      payload,
    }: {
      rankedChoice: number
      payload: Pick<
        NeighborhoodOccupantPayload,
        'map_id' | 'character_id' | 'user_id'
      >
    }) => deleteNeighborhoodOccupant(guildId, rankedChoice, payload),
    onSuccess: (data) => {
      updateCache(data)
      toast.success(t('shared.changes-saved'))
    },
    onError,
  })

  return {
    createChoice,
    updateChoice,
    deleteChoice,
    isPending:
      createChoice.isPending ||
      updateChoice.isPending ||
      deleteChoice.isPending,
  }
}
